import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Eye, CheckCheck, AlertTriangle, Ship, Receipt, FileText, Package, ExternalLink, Briefcase, CheckCircle, MessageSquare, ClipboardList } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import api from '@/services/api'
import { cn } from '@/lib/utils'

interface Notification {
  id: number
  title: string
  message: string
  priority: string
  category: string
  notification_type: string
  reference_type: string
  reference_id: number
  metadata: Record<string, unknown>
  is_read: boolean
  created_at: string
}

const referenceTypeIcons: Record<string, typeof AlertTriangle> = {
  job_header: Briefcase,
  service_approval: CheckCircle,
  quotation: FileText,
  discussion_job: MessageSquare,
  discussion_booking: MessageSquare,
  discussion_shipment: MessageSquare,
  discussion_bl: MessageSquare,
  booking: ClipboardList,
  shipment: Ship,
  invoice: Receipt,
  container: Package,
  sla_breach: AlertTriangle,
  shipment_stage_timeout: Ship,
  invoice_overdue: Receipt,
  delivery_deadline_at_risk: Ship,
  container_dwell_exceeded: Package,
  document_deadline_missed: FileText
}

function getNotificationRoute(notif: Notification): string {
  const type = notif.reference_type?.toLowerCase() || ''
  const id = notif.reference_id

  if (type === 'job_header') return '/dashboard/jobs/' + id
  if (type === 'service_approval') return '/dashboard/my-office'
  if (type === 'quotation') return '/dashboard/my-office'
  if (type === 'booking') return '/dashboard/bookings/' + id
  if (type === 'shipment') return '/dashboard/shipments/' + id
  if (type === 'invoice') return '/dashboard/invoices/' + id
  if (type === 'container') return '/dashboard/containers/' + id
  if (type === 'discussion_job') return '/dashboard/jobs/' + id
  if (type === 'discussion_booking') return '/dashboard/bookings/' + id
  if (type === 'discussion_shipment') return '/dashboard/shipments/' + id
  if (type === 'discussion_bl') return '/dashboard/bl/' + id
  if (type === 'sla_breach' || type === 'shipment_stage_timeout' || type === 'invoice_overdue' || type === 'delivery_deadline_at_risk' || type === 'container_dwell_exceeded' || type === 'document_deadline_missed') return '/dashboard/sla-monitor'

  return '/dashboard/sla-monitor'
}

function getNotificationIcon(notif: Notification) {
  const type = notif.reference_type?.toLowerCase() || ''
  if (referenceTypeIcons[type]) return referenceTypeIcons[type]

  const breachType = (notif.metadata as Record<string, unknown>)?.breachType as string
  if (breachType && referenceTypeIcons[breachType]) return referenceTypeIcons[breachType]

  return AlertTriangle
}

function getPriorityColor(priority: string): string {
  const p = priority?.toUpperCase() || ''
  if (p === 'URGENT') return 'text-red-500'
  if (p === 'HIGH') return 'text-orange-500'
  if (p === 'MEDIUM') return 'text-blue-500'
  return 'text-muted-foreground'
}

function getTimeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return diffMin + 'm ago'
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return diffHours + 'h ago'
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return diffDays + 'd ago'
  return date.toLocaleDateString()
}

export function NotificationBell() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications/in-app?limit=10&is_read=false')
      if (res?.success) {
        const items = res.data || []
        setNotifications(items)
        setUnreadCount(res.unread || items.length)
      }
    } catch {
      // silent
    }
  }, [])

  useEffect(() => {
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 60000)
    return () => clearInterval(interval)
  }, [fetchNotifications])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const markAsRead = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await api.patch(`/notifications/in-app/${id}/read`)
      setNotifications(prev => prev.filter(n => n.id !== id))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch {
      // silent
    }
  }

  const markAllRead = async () => {
    try {
      await api.patch('/notifications/in-app/read-all')
      setNotifications([])
      setUnreadCount(0)
    } catch {
      // silent
    }
  }

  const handleNotifClick = (notif: Notification) => {
    setOpen(false)
    markAsRead(notif.id, { stopPropagation: () => {} } as React.MouseEvent).catch(() => {})
    navigate(getNotificationRoute(notif))
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        className="hover:bg-accent relative"
        onClick={() => setOpen(!open)}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4.5 w-4.5 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[480px] rounded-xl border bg-popover shadow-xl z-50 overflow-hidden animate-fade-in">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
              {unreadCount > 0 && (
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                  {unreadCount}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7 text-muted-foreground hover:text-foreground"
                  onClick={markAllRead}
                  title="Mark all as read"
                >
                  <CheckCheck className="h-3.5 w-3.5 mr-1" />
                  Read All
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={() => { setOpen(false); navigate('/dashboard/sla-monitor') }}
              >
                View All
                <ExternalLink className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[380px]">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No new notifications</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = getNotificationIcon(notif)
                return (
                  <div
                    key={notif.id}
                    className="group flex items-start gap-3 px-4 py-3 border-b last:border-b-0 hover:bg-accent/50 cursor-pointer transition-colors bg-accent/20"
                    onClick={() => handleNotifClick(notif)}
                  >
                    <div className={cn('mt-0.5 flex-shrink-0', getPriorityColor(notif.priority))}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground">{notif.title}</p>
                      <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-xs text-muted-foreground/70 mt-1">
                        {getTimeAgo(notif.created_at)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => markAsRead(notif.id, e)}
                      title="Mark as read"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </div>
  )
}
